import { IconAngle, IconDroplet, IconTemperature } from "@tabler/icons-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  formatSpecificGravity,
  formatTemperature,
  formatTiltAngle,
} from "@/lib/ble/parsers"
import type { DeviceSettings, SensorReading } from "@/types/device"

interface ReadingsPanelDesktopProps {
  reading: SensorReading
  settings: DeviceSettings | null
}

export function ReadingsPanelDesktop({ reading, settings }: ReadingsPanelDesktopProps) {
  const tempUnit = settings?.temp_unit ?? "fahrenheit"

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">Hydrometer Readings</h3>
        <p className="text-sm text-muted-foreground">
          Real-time specific gravity and temperature data
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <IconDroplet className="size-4 text-blue-500" />
              <CardDescription>Specific Gravity</CardDescription>
            </div>
            <CardTitle className="text-3xl tabular-nums">
              {formatSpecificGravity(reading.specificGravity)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {reading.specificGravity
                ? `${((reading.specificGravity - 1) * 1000).toFixed(1)} points above water`
                : "No data"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <IconTemperature className="size-4 text-orange-500" />
              <CardDescription>Temperature</CardDescription>
            </div>
            <CardTitle className="text-3xl tabular-nums">
              {formatTemperature(reading.temperature, tempUnit)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {reading.temperature
                ? `${formatTemperature(reading.temperature, tempUnit === "fahrenheit" ? "celsius" : "fahrenheit")} (${tempUnit === "fahrenheit" ? "C" : "F"})`
                : "No data"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <IconAngle className="size-4 text-purple-500" />
              <CardDescription>Tilt Angle</CardDescription>
            </div>
            <CardTitle className="text-3xl tabular-nums">
              {formatTiltAngle(reading.tiltAngle)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {reading.tiltAngle ? "Device orientation" : "No data"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
